import React, { useContext, useEffect, useState } from 'react';
import {
  StyledCheckbox, StyledContainer, StyledGroupWrapper,
  StyledColumnGroupWrapper, StyledInputLabel,
  StyledInputNumberWrapper, StyledTitle, StyledColumnGroupToTopWrapper
} from './Styles';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../../stores/RootStoreContext';
import styled from 'styled-components';
import { Tooltip } from 'antd';

const HorizontalWrapper = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  
  > * {
    flex: 1;
  }
`;

const ErrorWrapper = styled.div`
  position: relative;
  min-height: 20px;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  color: #ff4d4f;
  font-size: 12px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  
  &::before {
    content: "⚠";
    margin-right: 4px;
    color: #ff4d4f;
  }
`;

interface ValidationError {
  allVendors?: string;
  indeed?: string;
  linkedIn?: string;
  craiglist?: string;
}

const MAX_CAP_VALUE = 150; // Configurable by request



const Caps = () => {

  const { attributesManagementStore } = useContext(RootStoreContext);
  const { CompanyAttributesAggregation } = attributesManagementStore;
  const { allVendorsCap, indeedCap, linkedInCap, craiglistBudget } = CompanyAttributesAggregation;

  const [validationErrors, setValidationErrors] = useState<ValidationError>({});

  const validateCapValue = (value: any, fieldName: keyof ValidationError): string | null => {
    // If empty, return null (no error) - will be set to 0
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    // Check if not a number
    if (isNaN(value) || !Number.isInteger(Number(value))) {
      return 'Enter numbers only.';
    }
    
    const numValue = Number(value);
    
    // Check if less than 0
    if (numValue < 0) {
      return 'Value must be 0 or greater.';
    }
    
    // Check if more than max (configurable)
    if (numValue > MAX_CAP_VALUE) {
      return `Value must be ${MAX_CAP_VALUE} or less.`;
    }
    
    return null;
  };

  const updateValidationError = (fieldName: keyof ValidationError, error: string | null) => {
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };


  useEffect(() => {
    // Notify store about LinkedIn validation errors
    attributesManagementStore.setHasLinkedInValidationErrors(!!validationErrors.linkedIn);
  }, [validationErrors.linkedIn]);


  const onChangeAllVendorsCap = (value: any) => {
    if (allVendorsCap) {
      allVendorsCap.cap = value;
      attributesManagementStore.onChangeAttribute(allVendorsCap);
    }
  }

  const onIsOriginalsOnlyAllVendors = () => {
    if (allVendorsCap) {
      allVendorsCap.isOriginalsOnly = !allVendorsCap.isOriginalsOnly;
      attributesManagementStore.onChangeAttribute(allVendorsCap);
    }
  }

  const onChangeIndeedCap = (value: any) => {
    if (indeedCap) {   
      indeedCap.cap = value;
      attributesManagementStore.onChangeAttribute(indeedCap);    
    }
  }

  const onIsOriginalsOnly = () => {
    if (indeedCap) {
      indeedCap.isOriginalsOnly = !indeedCap.isOriginalsOnly;
      attributesManagementStore.onChangeAttribute(indeedCap);
    }
  }

  const onChangeLinkedInCap = (value: any) => {
    if (linkedInCap) {
      // Set to 0 if empty, otherwise use the value
      const finalValue = (value === null || value === undefined || value === '') ? 0 : Number(value);
      linkedInCap.dailyBudgetPercentP4P = finalValue;
      attributesManagementStore.onChangeAttribute(linkedInCap);
      // Clear error when user is typing
      if (validationErrors.linkedIn) {
        updateValidationError('linkedIn', null);
      }
    }
  }

  const onBlurLinkedInCap = () => {
    if (linkedInCap) {
      const error = validateCapValue(linkedInCap.dailyBudgetPercentP4P, 'linkedIn');
      updateValidationError('linkedIn', error);
    }
  }

  const onIsOriginalsOnlyLinkedIn = () => {
    if (linkedInCap) {
      linkedInCap.isOriginalsOnly = !linkedInCap.isOriginalsOnly;
      attributesManagementStore.onChangeAttribute(linkedInCap);
    }
  }
  ///

  const onChangeCraiglistCap = (value: any) => {
    if (craiglistBudget) {
      craiglistBudget.budget = value;
      attributesManagementStore.onChangeAttribute(craiglistBudget);
    }
  }


  return (
    <>
      <StyledContainer>
        <StyledTitle>
          <Tooltip title="Limits the number of exported jobs">
            <span>Carousel Cap</span>
          </Tooltip>
        </StyledTitle>
        <StyledGroupWrapper>
          <StyledColumnGroupWrapper>
            <StyledInputLabel>All Vendors</StyledInputLabel>
            <StyledInputNumberWrapper min={0}
              onChange={onChangeAllVendorsCap}
              value={allVendorsCap?.cap} />
            <StyledCheckbox
              onClick={onIsOriginalsOnlyAllVendors}
              checked={allVendorsCap?.isOriginalsOnly} >Original Only</StyledCheckbox>
            <ErrorWrapper />
          </StyledColumnGroupWrapper>
          <StyledColumnGroupWrapper>
            <StyledInputLabel>Indeed</StyledInputLabel>
            <StyledInputNumberWrapper min={0}
              onChange={onChangeIndeedCap}
              value={indeedCap?.cap} />
            <StyledCheckbox
              onClick={onIsOriginalsOnly}
              checked={indeedCap?.isOriginalsOnly} >Original Only</StyledCheckbox>
            <ErrorWrapper />
          </StyledColumnGroupWrapper>
        </StyledGroupWrapper>
      </StyledContainer>

      <StyledContainer>
        <StyledTitle>
          <Tooltip title="Limits the spend per vendor. (LinkedIn - daily spend share, Craigslist - cost per post)">
            <span>Vendor's Budget</span>
          </Tooltip>
        </StyledTitle>
        <StyledGroupWrapper>
          <StyledColumnGroupToTopWrapper>
            <StyledInputLabel>LinkedIn BU %</StyledInputLabel>
            <StyledInputNumberWrapper
              onChange={onChangeLinkedInCap}
              onBlur={onBlurLinkedInCap}
              value={linkedInCap?.dailyBudgetPercentP4P} 
              status={validationErrors.linkedIn ? 'error' : undefined} />
            <StyledCheckbox
              onClick={onIsOriginalsOnlyLinkedIn}
              checked={linkedInCap?.isOriginalsOnly} >Original Only</StyledCheckbox>
            <ErrorWrapper>
              {validationErrors.linkedIn && <ErrorMessage>{validationErrors.linkedIn}</ErrorMessage>}
            </ErrorWrapper>
          </StyledColumnGroupToTopWrapper>
          <StyledColumnGroupToTopWrapper>
            <StyledInputLabel>Craigslist budget $</StyledInputLabel>
            <StyledInputNumberWrapper min={0}
              onChange={onChangeCraiglistCap}
              value={craiglistBudget?.budget} />
            <div style={{ height: '32px' }} />
            <ErrorWrapper />
          </StyledColumnGroupToTopWrapper>
        </StyledGroupWrapper>
      </StyledContainer>
    </>
  );
};

export default observer(Caps);
